import { useCallback, useEffect, useRef, useState } from 'react'

export type ScrollFadeOrientation = 'horizontal' | 'vertical'

export interface ScrollFadeEdges {
  /** Hay contenido oculto ANTES del viewport (izquierda en horizontal, arriba en vertical). */
  start: boolean
  /** Hay contenido oculto DESPUÉS del viewport (derecha en horizontal, abajo en vertical). */
  end: boolean
}

/**
 * Headless: mide si un elemento scrolleable tiene contenido oculto en cada borde.
 * Devuelve un `ref` para el nodo que scrollea (el que tiene `overflow-*-auto`) y el
 * estado `edges` — verdadero del lado donde queda contenido cortado. No pinta nada:
 * el consumidor decide qué renderizar con esa info (patrón "scroll fade" nativo).
 *
 * Se recalcula ante scroll, resize del contenedor (ResizeObserver) y tras cada render
 * (cambios de contenido: tabs/pasos/filtros que entran o salen). El `setEdges` corta
 * si el valor no cambió, así el recálculo por render no dispara renders en cadena.
 */
export function useScrollFade<T extends HTMLElement = HTMLElement>(
  orientation: ScrollFadeOrientation = 'horizontal'
) {
  const ref = useRef<T>(null)
  const [edges, setEdges] = useState<ScrollFadeEdges>({ start: false, end: false })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const [pos, viewport, total] =
      orientation === 'horizontal'
        ? [el.scrollLeft, el.clientWidth, el.scrollWidth]
        : [el.scrollTop, el.clientHeight, el.scrollHeight]
    setEdges((prev) => {
      const start = pos > 1
      const end = pos + viewport < total - 1
      return prev.start === start && prev.end === end ? prev : { start, end }
    })
  }, [orientation])

  // Medición atada a EVENTOS, nunca al render: hacerlo por render (useLayoutEffect que
  // setea estado) provoca un loop de updates cuando el ancho está en movimiento (p.ej.
  // el side panel animando su resize). Aquí:
  //  · scroll del elemento           → recalcula posición.
  //  · ResizeObserver del elemento   → resize del contenedor (side panel / viewport).
  //  · ResizeObserver de los hijos   → cambios de ancho del CONTENIDO (el propio
  //    elemento recorta con overflow, así que su caja no refleja el desborde).
  //  · MutationObserver (childList)  → hijos que entran/salen (tabs/pasos/filtros):
  //    re-observa el nuevo set y re-mide.
  useEffect(() => {
    const el = ref.current
    if (!el) return

    const resizeObserver = new ResizeObserver(measure)
    const observeAll = () => {
      resizeObserver.disconnect()
      resizeObserver.observe(el)
      for (const child of Array.from(el.children)) resizeObserver.observe(child)
    }
    observeAll()

    const mutationObserver = new MutationObserver(() => {
      observeAll()
      measure()
    })
    mutationObserver.observe(el, { childList: true })

    el.addEventListener('scroll', measure, { passive: true })
    measure()

    return () => {
      el.removeEventListener('scroll', measure)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [measure])

  return { ref, edges }
}
